import React, { useEffect, useRef } from 'react'
import { getWordAtCell } from '../../utils/gridUtils'

const styles = {
  container: {
    outline: 'none',
  },
  grid: {
    display: 'inline-block',
    border: '2px solid var(--color-primary)',
  },
  row: {
    display: 'flex',
  },
  cell: {
    width: '32px',
    height: '32px',
    border: '1px solid var(--color-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    position: 'relative',
    background: '#fff',
    fontSize: '16px',
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  cellBlack: {
    background: 'var(--color-primary)',
    cursor: 'pointer',
  },
  cellSelected: {
    background: 'var(--color-warning)',
  },
  cellHighlighted: {
    background: '#e6f3ff',
  },
  cellNumber: {
    position: 'absolute',
    top: '1px',
    left: '2px',
    fontSize: '9px',
    fontWeight: '700',
    lineHeight: 1,
  },
}

export default function InteractiveEditorGrid({
  grid,
  numberedCells,
  selectedCell,
  setSelectedCell,
  direction,
  setDirection,
  onKeyDown,
}) {
  const containerRef = useRef(null)

  useEffect(() => {
    containerRef.current?.focus()
  }, [])

  if (!grid) return null

  // Get highlighted cells (the current word) - only if cell is not black
  const highlightedCells = new Set()
  if (selectedCell && !grid[selectedCell.row][selectedCell.col].isBlack) {
    const word = getWordAtCell(grid, selectedCell.row, selectedCell.col, direction)
    if (word) {
      word.cells.forEach(c => highlightedCells.add(`${c.row}-${c.col}`))
    }
  }

  const handleCellClick = (row, col) => {
    if (selectedCell?.row === row && selectedCell?.col === col) {
      // Toggle direction on re-click (only if not a black cell)
      if (!grid[row][col].isBlack) {
        setDirection(d => d === 'across' ? 'down' : 'across')
      }
    } else {
      setSelectedCell({ row, col })
    }
  }

  return (
    <div
      ref={containerRef}
      style={styles.container}
      tabIndex={0}
      onKeyDown={onKeyDown}
    >
      <div style={styles.grid}>
        {grid.map((row, rowIdx) => (
          <div key={rowIdx} style={styles.row}>
            {row.map((cell, colIdx) => {
              const key = `${rowIdx}-${colIdx}`
              const isSelected = selectedCell?.row === rowIdx && selectedCell?.col === colIdx
              const isHighlighted = highlightedCells.has(key) && !isSelected
              const cellNum = numberedCells.cells[key]

              let cellStyle = { ...styles.cell }
              if (cell.isBlack) {
                cellStyle = { ...cellStyle, ...styles.cellBlack }
                if (isSelected) {
                  cellStyle = { ...cellStyle, outline: '3px solid var(--color-accent)', outlineOffset: '-3px' }
                }
              } else if (isSelected) {
                cellStyle = { ...cellStyle, ...styles.cellSelected }
              } else if (isHighlighted) {
                cellStyle = { ...cellStyle, ...styles.cellHighlighted }
              }

              return (
                <div
                  key={colIdx}
                  style={cellStyle}
                  onClick={() => handleCellClick(rowIdx, colIdx)}
                >
                  {cellNum && !cell.isBlack && (
                    <span style={styles.cellNumber}>{cellNum}</span>
                  )}
                  {!cell.isBlack && cell.letter}
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
